import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { Modal, Button } from "react-bootstrap";
import "leaflet/dist/leaflet.css";
import { QRCodeCanvas } from "qrcode.react";

const API_URL = "https://sunrise-backend.sunrisemeditechsystems.com/api";

export default function MyDeliveries() {
  const navigate = useNavigate();
  // Separate states for delivery and return orders
  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const [returnOrders, setReturnOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedDeliverOrder, setSelectedDeliverOrder] = useState(null);

  const [showOrderModal, setShowOrderModal] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showDeliverModal, setShowDeliverModal] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrValue, setQrValue] = useState("");

  const locationWatchers = useRef({});

  useEffect(() => {
    const userDataStr = localStorage.getItem("userData");
    if (!userDataStr) {
      navigate("/login");
      return;
    }
    const userData = JSON.parse(userDataStr);
    console.log("MyDeliveries - User data:", userData);
    if (userData.role !== "deliveryman") {
      navigate("/");
      return;
    }
    fetchAssignedOrders();
  }, [navigate]);

  const fetchAssignedOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      console.log("MyDeliveries - Using token:", token);

      // Only fetch orders assigned to this deliveryman
      const response = await axios.get(`${API_URL}/order/assigned/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const allOrders = response.data;
      console.log("Fetched assigned orders:", allOrders);

      setDeliveryOrders(
        allOrders.filter((o) =>
          ["assigned", "out_for_delivery"].includes(o.status)
        )
      );
      setReturnOrders(
        allOrders.filter((o) =>
          ["return_pending", "out_for_return"].includes(o.status)
        )
      );
      setCompletedOrders(
        allOrders.filter((o) => ["delivered", "returned"].includes(o.status))
      );
      setLoading(false);
    } catch (error) {
      console.error("Error fetching assigned orders:", error);
      setLoading(false);
    }
  };

  // Modify renderOrderTable to include source indicator
  const renderOrderTable = (orders, title) => (
    <div className="card">
      <div className="card-body">
        <h4 className="header-title">{title}</h4>
        {orders.length === 0 ? (
          <p className="text-muted">No orders in this section.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-centered table-hover mb-0">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td>
                      <strong>{order.id}</strong>
                    </td>
                    <td>
                      {order.source === "sunrise-website"
                        ? `${order.shipping_address?.firstName || "Guest"} ${
                            order.shipping_address?.lastName || ""
                          }`
                        : `${
                            order.shipping_address?.doctorName || "Doctor"
                          } - ${
                            order.shipping_address?.hospitalName || "Hospital"
                          }`}
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          order.source === "sunrise-website"
                            ? "bg-info text-white"
                            : "bg-primary text-white"
                        }`}
                      >
                        {order.source === "sunrise-website"
                          ? "Website"
                          : "WebApp"}
                      </span>
                    </td>
                    <td>{getStatusBadge(order.status)}</td>
                    <td>
                      <div className="d-flex gap-8">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleOrderClick(order, "")}
                        >
                          Details
                        </button>
                        {renderActionButtons(order)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  // ** FIX: Corrected getStatusBadge function **
  const getStatusBadge = (status) => {
    const statusConfig = {
      assigned: { class: "bg-success", text: "Assigned" },
      out_for_delivery: { class: "bg-info", text: "Out for Delivery" },
      return_pending: { class: "bg-warning", text: "Return Pending" },
      out_for_return: { class: "bg-info", text: "Out for Return" },
      delivered: { class: "bg-success", text: "Delivered" },
      returned: { class: "bg-success", text: "Returned" },
    };
    const config = statusConfig[status] || {
      class: "bg-secondary",
      text: status,
    };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  //  const handleStatusUpdate = async (orderId, order, newStatus) => {
  //     try {
  //       setUpdatingStatus(orderId);
  //       const token = localStorage.getItem("token");
  //       await axios.put(`${API_URL}/orders/${orderId}/status`, { status: newStatus }, {
  //         headers: { Authorization: `Bearer ${token}` }
  //       });
  //       await fetchAssignedOrders();
  //       if (showOrderModal) closeModal();
  //       Swal.fire('Success', `Order status updated to ${newStatus}`, 'success');
  //     } catch (error) {
  //       Swal.fire('Error', error.response?.data?.message || 'Failed to update order status', 'error');
  //     } finally {
  //       setUpdatingStatus(null);
  //     }
  //   };

  const handleStatusUpdate = async (orderId, order, newStatus) => {
    try {
      setSelectedDeliverOrder(order);
      setSelectedOrder(order);
      setUpdatingStatus(orderId);
      const qrData = JSON.stringify({
        orderId: order?.id,
        productId: order?.items && order?.items[0]?.product,
        rentalStartDate: order?.items && order?.items[0]?.rentalStartDate,
        rentalEndDate: order?.items && order?.items[0]?.rentalEndDate,
        title: "deliver",
      });
      setQrValue(qrData);
      if (newStatus == "delivered" ) {
        setUpdatingStatus("delivered");
      }

      const token = localStorage.getItem("token");
      await axios.patch(
        `${API_URL}/orders/${orderId}/status`,
        { status: newStatus },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if ((newStatus = "out_for_delivery" && newStatus != "delivered")) {
        await fetchAssignedOrders();
      }
      if (showDeliverModal) {
        setShowDeliverModal(false);
        setShowQRModal(false);
        await fetchAssignedOrders();
        Swal.fire("Success", `Order status updated to true`, "success");
      }
    } catch (error) {
      console.log(error);    
    } finally {
      setUpdatingStatus(null);
      if (showDeliverModal) {
        setShowDeliverModal(false);
        setShowQRModal(false);
      }
    }
  };

  const handlePhotoUpload = async (orderId, photoType) => {
    if (!selectedFile) return;
    try {
      setUploadingPhoto(true);
      const token = localStorage.getItem("token");
      if (photoType == "return") {
        const formData = new FormData();
        formData.append("photo", selectedFile);
        formData.append("photo_type", "return"); // Hardcode to 'return' for this flow
        const response = await axios.post(
          `${API_URL}/orders/${orderId}/photo`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        // Refresh the order in the modal to enable the complete button
        setSelectedOrder(response.data.order);
        setSelectedFile(null); // Clear the file input

        document.getElementById("file-input-return").value = ""; // Reset file input visually
      } else {
        const formData = new FormData();
        formData.append("photo", selectedFile);
        formData.append("photo_type", "delivery"); // Hardcode to 'return' for this flow
        const response = await axios.post(
          `${API_URL}/orders/${orderId}/photo`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        console.log("order_detail",response.data.order)
        // Refresh the order in the modal to enable the complete button
        setSelectedDeliverOrder(response.data.order);
        setSelectedFile(null); // Clear the file input
        document.getElementById("file-input-deliver").value = ""; // Reset file input visually
      }
      // Swal.fire('Success', 'Return photo uploaded!', 'success');
    } catch (error) {
      // Swal.fire('Error', 'Failed to upload photo.', 'error');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // ** FIX: Added missing handleOrderClick function **
  const handleOrderClick = (order, modelType) => {
    setSelectedOrder(order);
    const qrData = JSON.stringify({
      orderId: order?.id,
      productId: order?.items && order?.items[0]?.product,
      rentalStartDate: order?.items && order?.items[0]?.rentalStartDate,
      rentalEndDate: order?.items && order?.items[0]?.rentalEndDate,
      title: "return",
    });
    setQrValue(qrData);
    // Step 1: Show QR modal first
    if (modelType == "return_qr_model") {
      setShowQRModal(true);
      setShowOrderModal(false);
    }
  };

  const verifyQR = async () => {
    try {
      // Step 1: Parse QR value into JSON
      const parsed = JSON.parse(qrValue);
      // console.log("parsed", parsed);

      if (!parsed.orderId || !parsed.productId) {
        alert("❌ Invalid QR data");
        return;
      }

      // Step 2: Call backend API to verify
      const response = await fetch(
        "https://sunrise-backend.sunrisemeditechsystems.com/api/orders/verify-rental-order",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            order_id: parsed.orderId,
            product_id: parsed.productId,
          }),
        }
      );

      const data = await response.json();
      // Step 3: Check API response
      if (data.success == 1) {
        alert("✅ Verified!");
        setShowQRModal(false);
        if (parsed.title == "return") {
          setShowOrderModal(true);
        } else {
          setShowDeliverModal(true);
        }
      } else {
        alert(`❌ Verification failed: ${data.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error verifying QR:", error);
      alert("❌ Something went wrong while verifying QR");
    }
  };

  // Renders the action buttons in the table row
  const renderActionButtons = (order) => {
    switch (order.status) {
      case "out_for_delivery":
        return (
          <button
            className="btn btn-sm btn-success"
            onClick={() =>{ setShowQRModal(true); handleStatusUpdate(order.id, order, "delivered")}}
            disabled={updatingStatus === order.id}
          >
            {updatingStatus === order.id ? "..." : "Mark Delivered"}
          </button>
        );
      case "assigned":
        return (
          <button
            className="btn btn-sm btn-success"
            onClick={() =>
              handleStatusUpdate(order.id, order, "out_for_delivery")
            }
            disabled={updatingStatus === order.id}
          >
            {updatingStatus === order.id ? "..." : "Start Delivery"}
          </button>
        );
      // case 'out_for_delivery':
      //   return <button className="btn btn-sm btn-success" onClick={() => handleNewStatus(order._id, order, 'delivered')} disabled={updatingStatus === order._id}>{updatingStatus === order._id ? '...' : 'Mark Delivered'}</button>;

      case "return_pending":
        return (
          <button
            className="btn btn-sm btn-warning"
            onClick={() =>
              handleStatusUpdate(order.id, order, "out_for_return")
            }
            disabled={updatingStatus === order.id}
          >
            {updatingStatus === order.id ? "..." : "Start Return"}
          </button>
        );
      case "out_for_return":
        return (
          <button
            className="btn btn-sm btn-info"
            onClick={() => handleOrderClick(order, "return_qr_model")}
          >
            Complete Return
          </button>
        );
      default:
        return null;
    }
  };

  const closeModal = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
  };
  const handleFileSelect = (e) => setSelectedFile(e.target.files[0]);

  // Start location tracking for active orders
  useEffect(() => {
    // Only track for orders that are out_for_delivery or out_for_return
    const activeOrders = [...deliveryOrders, ...returnOrders].filter((o) =>
      ["out_for_delivery", "out_for_return"].includes(o.status)
    );
    // console.log(activeOrders);
    activeOrders.forEach((order) => {
      if (!locationWatchers.current[order.id]) {
        // Start watching location
        locationWatchers.current[order.id] =
          navigator.geolocation.watchPosition(
            (pos) => {
              const { latitude, longitude } = pos.coords;
              // Send to backend
              const token = localStorage.getItem("token");
              axios
                .post(
                  `https://sunrise-backend.sunrisemeditechsystems.com/api/orders/${order.id}/location`,
                  { latitude, longitude },
                  { headers: { Authorization: `Bearer ${token}` } }
                )
                .catch(() => {});
            },
            (err) => {
              /* handle error if needed */
            },
            { enableHighAccuracy: true, maximumAge: 10000, timeout: 10000 }
          );
      }
    });

    // Cleanup: stop watchers for orders that are no longer active
    return () => {
      Object.keys(locationWatchers.current).forEach((orderId) => {
        if (!activeOrders.find((o) => o.id === orderId)) {
          navigator.geolocation.clearWatch(locationWatchers.current[orderId]);
          delete locationWatchers.current[orderId];
        }
      });
    };
  }, [deliveryOrders, returnOrders]);

  if (loading)
    return (
      <div className="text-center p-5">
        <div className="spinner-border"></div>
      </div>
    );

  return (
    <div className="content-page">
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="page-title-box">
                <h4 className="page-title">My Tasks</h4>
              </div>
            </div>
          </div>
          <div className="row">
            {renderOrderTable(deliveryOrders, "Current Deliveries")}
          </div>
          <div className="row mt-4">
            {renderOrderTable(returnOrders, "Current Return Pickups")}
          </div>
          <div className="row mt-4">
            {renderOrderTable(
              completedOrders.slice(0, 5),
              "Recently Completed Deliveries & Returns"
            )}
          </div>
        </div>
      </div>
      {/* QR Code Modal */}

      <Modal
        show={showQRModal}
        onHide={() => setShowQRModal(false)}
        centered
        className="qr-modal"
      >
        <Modal.Header className="qr-modal-header">
          <button
            type="button"
            onClick={() => setShowQRModal(false)}
            className="order-btn"
          >
            ×
          </button>
          <Modal.Title className="qr-title">Scan & Verify QR Code</Modal.Title>
        </Modal.Header>

        <Modal.Body className="text-center qr-modal-body">
          <QRCodeCanvas value={qrValue} size={180} />
          <br />
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="primary"
            onClick={verifyQR} // ❌ Disabled until scan
          >
            Verify & Continue
          </Button>
        </Modal.Footer>
      </Modal>

      {showOrderModal &&
        selectedOrder &&
        JSON.parse(qrValue).title == "return" && (
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Order Return Details</h5>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="order-btn"
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <p>
                    <strong>Order ID:</strong> {selectedOrder.id}
                  </p>
                  <p>
                    <strong>Customer:</strong>{" "}
                    {selectedOrder.source === "sunrise-website"
                      ? `${
                          selectedOrder.shipping_address?.firstName || "Guest"
                        } ${selectedOrder.shipping_address?.lastName || ""}`
                      : `${
                          selectedOrder.shipping_address?.doctorName || "Doctor"
                        } - ${
                          selectedOrder.shipping_address?.hospitalName ||
                          "Hospital"
                        }`}
                  </p>
                  <p>
                    <strong>Source:</strong>{" "}
                    <span
                      className={`badge ${
                        selectedOrder.source === "sunrise-website"
                          ? "bg-info"
                          : "bg-primary"
                      }`}
                    >
                      {selectedOrder.source === "sunrise-website"
                        ? "Website"
                        : "WebApp"}
                    </span>
                  </p>

                  {selectedOrder.status === "out_for_return" && (
                    <div className="mt-4 p-3 border-top">
                      <h6 className="fw-bold">turn Process</h6>
                      <p className="text-muted">
                        Please upload a photo of the item's condition to
                        complete the return.
                      </p>

                      <div className="d-flex gap-2 align-items-center mb-3">
                        <input
                          id="file-input-return"
                          type="file"
                          className="form-control"
                          accept="image/*"
                          onChange={handleFileSelect}
                        />
                        <button
                          className="btn btn-primary"
                          onClick={() =>
                            handlePhotoUpload(selectedOrder?.id, "return")
                          }
                          disabled={!selectedFile || uploadingPhoto}
                        >
                          {uploadingPhoto && selectedFile
                            ? "Uploading..."
                            : "Upload Photo"}
                        </button>
                      </div>

                      {selectedOrder.return_photos &&
                        selectedOrder.return_photos.length > 0 && (
                          <p className="text-success small">
                            ✓ Photo uploaded successfully.
                          </p>
                        )}

                      <button
                        className="btn btn-success w-100"
                        onClick={() =>
                          handleStatusUpdate(
                            selectedOrder.id,
                            selectedOrder,
                            "returned"
                          )
                        }
                        disabled={
                          !selectedOrder.return_photos ||
                          selectedOrder.return_photos.length === 0 ||
                          updatingStatus
                        }
                      >
                        {updatingStatus ? "..." : "Mark as Returned"}
                      </button>
                    </div>
                  )}

                  {["out_for_delivery", "out_for_return"].includes(
                    selectedOrder.status
                  ) &&
                  selectedOrder.return_photos &&
                    selectedOrder.delivery_location &&
                    selectedOrder.delivery_location.latitude &&
                    selectedOrder.delivery_location.longitude && (
                      <div
                        style={{ height: 300, width: "100%", margin: "20px 0" }}
                      >
                        <MapContainer
                          center={[
                            selectedOrder.delivery_location.latitude,
                            selectedOrder.delivery_location.longitude,
                          ]}
                          zoom={15}
                          style={{ height: "100%", width: "100%" }}
                        >
                          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                          <Marker
                            position={[
                              selectedOrder.delivery_location.latitude,
                              selectedOrder.delivery_location.longitude,
                            ]}
                          >
                            <Popup>
                              <strong>
                                {selectedOrder.assigned_deliveryman?.name ||
                                  "Deliveryman"}
                                's Location
                              </strong>
                              <br />
                              {selectedOrder.delivery_location.address || ""}
                            </Popup>
                          </Marker>
                        </MapContainer>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        )}

      {showDeliverModal &&
        selectedOrder &&
        JSON.parse(qrValue).title == "deliver" && (
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Order Deliver Details</h5>
                  <button
                    type="button"
                    onClick={() => setShowDeliverModal(false)}
                    className="order-btn"
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <p>
                    <strong>Order ID:</strong> {selectedOrder?.id}
                  </p>
                  <p>
                    <strong>Customer:</strong>{" "}
                    {selectedOrder?.source === "sunrise-website"
                      ? `${
                          selectedOrder?.shipping_address?.firstName || "Guest"
                        } ${selectedOrder?.shipping_address?.lastName || ""}`
                      : `${
                          selectedOrder?.shipping_address?.doctorName ||
                          "Doctor"
                        } - ${
                          selectedOrder?.shipping_address?.hospitalName ||
                          "Hospital"
                        }`}
                  </p>
                  <p>
                    <strong>Source:</strong>{" "}
                    <span
                      className={`badge ${
                        selectedOrder?.source === "sunrise-website"
                          ? "bg-info"
                          : "bg-primary"
                      }`}
                    >
                      {selectedOrder?.source === "sunrise-website"
                        ? "Website"
                        : "WebApp"}
                    </span>
                  </p>

                  {selectedOrder?.status === "out_for_delivery" && (
                    <div className="mt-4 p-3 border-top">
                      <h6 className="fw-bold">turn Process</h6>
                      <p className="text-muted">
                        Please upload a photo of the item's condition to
                        complete the return.
                      </p>

                      <div className="d-flex gap-2 align-items-center mb-3">
                        <input
                          id="file-input-deliver"
                          type="file"
                          className="form-control"
                          accept="image/*"
                          onChange={handleFileSelect}
                        />
                        <button
                          className="btn btn-primary"
                          onClick={() =>
                            handlePhotoUpload(selectedOrder?.id, "deliver")
                          }
                          disabled={!selectedFile || uploadingPhoto}
                        >
                          {uploadingPhoto ? "Uploading..." : "Upload Photo"}
                        </button>
                      </div>

                      {selectedDeliverOrder?.delivery_photos &&
                        selectedDeliverOrder?.delivery_photos.length > 0 && (
                          <p className="text-success small">
                            ✓ Photo uploaded successfully.
                          </p>
                        )}

                      <button
                      name={console.log("select_order",selectedOrder,"delivery_order",selectedDeliverOrder)}
                        className="btn btn-success w-100"
                        onClick={() => {
                          setShowQRModal(false); // ✅ Close QR modal
                           setShowDeliverModal(false); // ✅ Close QR modal
                          handleStatusUpdate(
                            selectedDeliverOrder.id,
                            selectedDeliverOrder,
                            "delivered"
                          );
                        }}
                        disabled={
                          !selectedDeliverOrder?.delivery_photos ||
                          selectedDeliverOrder?.delivery_photos.length === 0 ||
                          updatingStatus
                        }
                      >
                        {updatingStatus ? "..." : "Mark as delivered"}
                      </button>
                    </div>
                  )}                 

                  {["out_for_delivery", "out_for_return"].includes(
                    selectedOrder.status
                  ) &&
                    selectedDeliverOrder?.delivery_location &&
                    selectedDeliverOrder?.delivery_location.latitude &&
                    selectedDeliverOrder?.delivery_location.longitude && (
                      <div
                        style={{ height: 300, width: "100%", margin: "20px 0" }}
                      >
                        <MapContainer
                          center={[
                            selectedDeliverOrder?.delivery_location.latitude,
                            selectedDeliverOrder?.delivery_location.longitude,
                          ]}
                          zoom={15}
                          style={{ height: "100%", width: "100%" }}
                        >
                          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                          <Marker
                            position={[
                              selectedDeliverOrder?.delivery_location.latitude,
                              selectedDeliverOrder?.delivery_location.longitude,
                            ]}
                          >
                            <Popup>
                              <strong>
                                {selectedOrder.assigned_deliveryman?.name ||
                                  "Deliveryman"}
                                's Location
                              </strong>
                              <br />
                              {selectedDeliverOrder?.delivery_location.address || ""}
                            </Popup>
                          </Marker>
                        </MapContainer>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
